import { useEffect, useState } from 'react'
import { apiClient } from '../lib/api'
import type { Thread, TimelineEntry, PaginatedResponse } from '../lib/types'
import { useCreatorStore } from '../stores/creatorStore'

interface Props {
  open: boolean
  collectionId: number
  onClose: () => void
}

export default function ItemSelector({ open, collectionId, onClose }: Props) {
  const [threads, setThreads] = useState<Thread[]>([])
  const [expandedThread, setExpandedThread] = useState<number | null>(null)
  const [entries, setEntries] = useState<TimelineEntry[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loadingThreads, setLoadingThreads] = useState(false)
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [adding, setAdding] = useState(false)
  const { addItem } = useCreatorStore()

  useEffect(() => {
    if (!open) return
    setLoadingThreads(true)
    apiClient
      .get<PaginatedResponse<Thread>>('research/threads/')
      .then((data) => setThreads(data.results))
      .finally(() => setLoadingThreads(false))
  }, [open])

  const handleExpandThread = async (threadId: number) => {
    if (expandedThread === threadId) {
      setExpandedThread(null)
      setEntries([])
      return
    }
    setExpandedThread(threadId)
    setLoadingEntries(true)
    const data = await apiClient.get<PaginatedResponse<TimelineEntry>>(
      `research/threads/${threadId}/timeline/`
    )
    setEntries(data.results)
    setLoadingEntries(false)
  }

  const toggleEntry = (entryId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(entryId)) next.delete(entryId)
      else next.add(entryId)
      return next
    })
  }

  const handleAddSelected = async () => {
    setAdding(true)
    for (const entryId of selected) {
      await addItem(collectionId, entryId)
    }
    setAdding(false)
    setSelected(new Set())
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-bg flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-elevated shrink-0">
        <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="font-medium">Add Items</h2>
        <button
          onClick={handleAddSelected}
          disabled={selected.size === 0 || adding}
          className="text-accent font-medium disabled:opacity-30"
        >
          {adding ? 'Adding...' : `Add (${selected.size})`}
        </button>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loadingThreads ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <p className="text-text-muted text-center mt-8">No threads found</p>
        ) : (
          threads.map((thread) => (
            <div key={thread.id}>
              <button
                onClick={() => handleExpandThread(thread.id)}
                className="w-full bg-surface rounded-lg p-3 text-left hover:bg-elevated transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate">{thread.title}</h3>
                    <p className="text-xs text-text-muted mt-0.5">
                      {thread.exchange_count} messages
                    </p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-text-muted transition-transform ${
                      expandedThread === thread.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </div>
              </button>

              {/* Expanded entries */}
              {expandedThread === thread.id && (
                <div className="ml-3 mt-1 space-y-1 border-l-2 border-elevated pl-3">
                  {loadingEntries ? (
                    <div className="py-4 flex justify-center">
                      <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : entries.length === 0 ? (
                    <p className="text-xs text-text-muted py-2">No entries</p>
                  ) : (
                    entries.map((entry) => {
                      const isSelected = selected.has(entry.id)
                      const excerpt = entry.exchange
                        ? entry.exchange.user_message.slice(0, 80)
                        : entry.content_item
                          ? (entry.content_item.summary || entry.content_item.processed_content).slice(0, 80)
                          : 'Entry'
                      return (
                        <button
                          key={entry.id}
                          onClick={() => toggleEntry(entry.id)}
                          className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                            isSelected
                              ? 'bg-accent/15 text-accent'
                              : 'bg-surface hover:bg-elevated text-text-secondary'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                                isSelected ? 'border-accent bg-accent' : 'border-text-muted'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-bg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                </svg>
                              )}
                            </div>
                            <span className="line-clamp-2">{excerpt}...</span>
                          </div>
                        </button>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
